// The event page body, as an attendee sees it: the blocks a club authored in
// BlockEditor, rendered read-only.
//
// Kept beside the editor rather than inside it so the viewer never pulls the
// editing UI into its bundle, and so there is exactly one definition of what a
// block LOOKS like — the editor imports the video embed from here rather than
// carrying a second copy that could drift.
import { VideoEmbed, imgStyle, imgFrameStyle } from './BlockEditor.jsx';
import { safeHref } from '../utils/safeUrl.js';

/**
 * Links are rendered with `rel="noopener noreferrer"`. A club types these, and a
 * `target="_blank"` link without `noopener` hands the opened page a live handle
 * on the tab it came from.
 */
export default function EventBody({ blocks }) {
  const list = (blocks || []).filter(b => b && b.type);
  if (!list.length) return null;
  return (
    <div className="eb-root">
      <style>{CSS}</style>
      {list.map((b, i) => {
        if (b.type === 'text') {
          if (!b.text) return null;
          // Blank lines separate paragraphs; a club typing into a textarea has
          // no other way to say "new paragraph".
          return (
            <div className="eb-text" key={i}>
              {String(b.text).split(/\n{2,}/).map((p, k) => <p key={k}>{p}</p>)}
            </div>
          );
        }
        if (b.type === 'image') {
          if (!b.url) return null;
          // The <figure> IS the frame: a narrowed picture carries its caption
          // with it, rather than leaving it stranded at full column width under
          // a half-width photo.
          return (
            <figure className="eb-fig" key={i} style={imgFrameStyle(b)}>
              <img src={b.url} alt={b.caption || ''} loading="lazy" style={imgStyle(b)} />
              {b.caption && <figcaption>{b.caption}</figcaption>}
            </figure>
          );
        }
        if (b.type === 'video') {
          if (!b.url) return null;
          return <div className="eb-video" key={i}><VideoEmbed url={b.url} autoplay={!!b.autoplay} /></div>;
        }
        if (b.type === 'link') {
          // The server already keeps these to http(s) and site paths; checked
          // again because an event saved before that rule is served as stored.
          const href = safeHref(b.url);
          if (!href) return null;
          return (
            <a className="eb-link" key={i} href={href} target="_blank" rel="noopener noreferrer">
              {b.text || b.url}
            </a>
          );
        }
        return null;
      })}
    </div>
  );
}

const CSS = `
.eb-root { display:flex; flex-direction:column; gap:12px; }
.eb-text p { margin:0; font-size:15px; line-height:1.6; }
/* A hairline between paragraphs rather than a gap.
   A club writes an event page as several unrelated things — what it is, who it
   is for, what to bring — and blank-line spacing alone leaves them running
   together into one grey wall on a phone. The rule says where one answer ends.
   It applies BETWEEN paragraphs only, so a single-paragraph event never gets a
   stray line, and it spans blocks as well as the paragraphs inside one, since
   whether a club pressed Enter twice or added a second text block is not a
   distinction a reader should be able to see. */
.eb-text p + p { margin-top:11px; padding-top:11px; border-top:1px solid #e8eaee; }
/* Between two text blocks the column's own 12px gap is already the space above
   the rule, so this one only pads underneath it. */
.eb-text + .eb-text { padding-top:11px; border-top:1px solid #e8eaee; }
.eb-fig { margin:0; }
.eb-fig img { width:100%; display:block; border-radius:12px; }
.eb-fig figcaption { margin-top:6px; font-size:12.5px; color:#6b7280; }
.eb-video { border-radius:12px; overflow:hidden; background:#000; }
.eb-link { display:block; text-align:center; background:#eef0fb; color:#3730a3;
  border-radius:11px; padding:12px 14px; font-weight:800; text-decoration:none; font-size:15px; }
.eb-link:hover { background:#e0e3f8; }
`;
